import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { MultiComponentCamera } from 'features/assetViz/cameras/MultiComponentCamera'
import { AxesHelper } from 'features/assetViz/objects/decor/AxesHelper'
import { ConeSurface3D } from 'features/assetViz/objects/surfaces/ConeSurface3D'
import { EllipsoidalHeadSurface3D } from 'features/assetViz/objects/surfaces/EllipsoidalHeadSurface3D'
import { FlatRoofSurface3D } from 'features/assetViz/objects/surfaces/FlatRoofSurface3D'
import { FloorSurface3D } from 'features/assetViz/objects/surfaces/FloorSurface3D'
import { HemisphericalHeadSurface3D } from 'features/assetViz/objects/surfaces/HemisphericalHeadSurface3D'
import { HoodBendSurface3D } from 'features/assetViz/objects/surfaces/HoodBendSurface3D'
import { HoodStraightSurface3D } from 'features/assetViz/objects/surfaces/HoodStraightSurface3D'
import { PipeEccentricReducerSurface3D } from 'features/assetViz/objects/surfaces/PipeEccentricReducerSurface3D'
import { PipeElbowSurface3D } from 'features/assetViz/objects/surfaces/PipeElbowSurface3D'
import { PipeSurface3D } from 'features/assetViz/objects/surfaces/PipeSurface3D'
import { PipeTJointSurface3D } from 'features/assetViz/objects/surfaces/PipeTJointSurface3D'
import { PressureVesselShellSurface3D } from 'features/assetViz/objects/surfaces/PressureVesselShellSurface3D'
import { ShellSurface3D } from 'features/assetViz/objects/surfaces/ShellSurface3D'
import { TorisphericalHeadSurface3D } from 'features/assetViz/objects/surfaces/TorisphericalHeadSurface3D'
import { getComponentPositionAndRotations } from 'features/assetViz/utils/multiComponentUtils'
import React, { ReactNode } from 'react'
import { GeckoAssetModel, GeckoComponentModelType } from 'types'

import { SceneFactoryConfig } from './sceneFactory'

export const MultiComponentSceneConfig: SceneFactoryConfig = {
  name: 'MultiComponent',
  multiComponentView: true,
  otherChecks: (props) => !!props.assetModel,
  fullSceneFunc: (props) =>
    props.assetModel ? (
      <MultiComponentScene
        assetModel={props.assetModel}
        showAxesHelper={props.showAxesHelper}
      />
    ) : null,
}

/**
 * A scene displaying all components with the proper scene configuration in an asset model
 * @param assetModel      The model used to represent the whole asset
 */
export const MultiComponentScene: React.FC<{
  assetModel: GeckoAssetModel
  showAxesHelper?: boolean
}> = ({ assetModel, showAxesHelper = false }) => {
  //----------------------------------------------------------------------------
  const { show3D } = useAssetVizInternal()
  const components3D = React.useMemo(() => {
    const components3D = new Array<ReactNode>()
    const components = [...assetModel.data.components.values()]

    for (let i = 0; i < components.length; i++) {
      const component = components[i]
      const { position, rotation } = getComponentPositionAndRotations(
        component,
        show3D
      )
      if (!position || !rotation) {
        continue // Skip components without valid position or rotation
      }
      const key = component.id
      if (component.type === GeckoComponentModelType.Cone) {
        components3D.push(
          <ConeSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.VerticalTankShell) {
        components3D.push(
          <ShellSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.TankFlatRoof) {
        components3D.push(
          <FlatRoofSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
            visible={!!position && !!rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.TankFloor) {
        components3D.push(
          <FloorSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.PipeSection) {
        components3D.push(
          <PipeSurface3D
            key={key}
            model={component}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.PipeElbow) {
        components3D.push(
          <PipeElbowSurface3D
            key={key}
            model={component}
            position={position}
            rotation={rotation}
            visible={true}
          />
        )
      } else if (component.type === GeckoComponentModelType.PipeTJoint) {
        components3D.push(
          <PipeTJointSurface3D
            key={key}
            model={component}
            position={position}
            rotation={rotation}
            visible={true}
          />
        )
      } else if (
        component.type === GeckoComponentModelType.PipeEccentricReducer
      ) {
        components3D.push(
          <PipeEccentricReducerSurface3D
            key={key}
            model={component}
            position={position}
            rotation={rotation}
            visible={true}
          />
        )
      } else if (
        component.type === GeckoComponentModelType.PressureVesselShell
      ) {
        components3D.push(
          <PressureVesselShellSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.HoodStraight) {
        components3D.push(
          <HoodStraightSurface3D
            key={key}
            model={component}
            position={position}
            rotation={rotation}
            visible={!!position && !!rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.HemisphericalHead) {
        components3D.push(
          <HemisphericalHeadSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.TorisphericalHead) {
        components3D.push(
          <TorisphericalHeadSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.EllipsoidalHead) {
        components3D.push(
          <EllipsoidalHeadSurface3D
            key={key}
            model={component}
            assetModel={assetModel}
            position={position}
            rotation={rotation}
          />
        )
      } else if (component.type === GeckoComponentModelType.HoodBend) {
        components3D.push(
          <HoodBendSurface3D
            key={key}
            model={component}
            position={position}
            rotation={rotation}
            visible={!!position && !!rotation}
          />
        )
      }
    }
    return components3D
  }, [assetModel, show3D])

  //----------------------------------------------------------------------------

  return (
    <>
      <MultiComponentCamera assetModel={assetModel} autoRecenter={false} />
      <AxesHelper scale={50} visible={showAxesHelper} />
      {components3D}
    </>
  )
}
